import { useEffect, useState } from "react";
import { cartTextSchema, parseCartText, type CartLine } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { eurosToCents } from "./Customers";
import { Icon, Message, Sheet } from "./ui";

const toText = (c: number) => (c / 100).toFixed(2).replace(".", ",");

interface Row {
  name: string;
  quantity: string;
  price: string;
  note: string | null;
  vatRate: 0 | 7 | 19;
}

/** Warenkorb einlesen: PDF, Screenshot oder eingefügter Text. Die erkannten Positionen werden vor der Übernahme geprüft. */
export function CartImportSheet(props: { onClose: () => void; onApply: (lines: CartLine[], file: File | null) => void }) {
  const [files, setFiles] = useState<boolean | null>(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [gross, setGross] = useState(true);
  const [keep, setKeep] = useState(true);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<{ files: boolean }>("/api/cart/status")
      .then((s) => setFiles(s.files))
      .catch(() => setFiles(false));
  }, []);

  function show(source: string) {
    const parsed = parseCartText(source, { pricesAre: gross ? "gross" : "net" });
    if (!parsed.length) {
      setError("Im Text habe ich keine Artikel mit Preis gefunden. Bitte prüfen Sie den Text, oder fügen Sie die Positionen im Beleg von Hand ein.");
      return;
    }
    setRows(parsed.map((l) => ({ name: l.name, quantity: String(l.quantity), price: toText(l.unitPriceCents), note: l.note, vatRate: l.vatRate })));
  }

  async function read() {
    setError("");
    setBusy(true);
    try {
      let source = text;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        source = (await upload<{ text: string }>("/api/cart/text", fd)).text;
        setText(source);
      }
      const ok = cartTextSchema.safeParse({ text: source });
      if (!ok.success) {
        setError(ok.error.issues[0]?.message ?? "Es wurde kein Text gefunden.");
        return;
      }
      show(source);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function apply() {
    const out: CartLine[] = [];
    for (const [i, r] of (rows ?? []).entries()) {
      const q = Number(r.quantity.replace(",", "."));
      const c = eurosToCents(r.price);
      if (!r.name.trim() || !Number.isFinite(q) || q <= 0 || c === null) {
        setError(`Bitte prüfen Sie Position ${i + 1}: Name, Menge und Preis müssen stimmen.`);
        return;
      }
      out.push({ name: r.name.trim(), quantity: q, unitPriceCents: c, vatRate: r.vatRate, note: null });
    }
    props.onApply(out, keep ? file : null);
  }

  function setRow(i: number, patch: Partial<Row>) {
    setRows((rs) => (rs ? rs.map((r, j) => (j === i ? { ...r, ...patch } : r)) : rs));
  }

  return (
    <Sheet title="Warenkorb einlesen" onClose={props.onClose}>
      {rows === null ? (
        <div className="cu-form">
          <p className="muted-s wide">
            Laden Sie ein PDF oder einen Screenshot Ihres Warenkorbs hoch, oder fügen Sie den Text daraus ein. Links zu Warenkörben kann HandVero nicht öffnen, weil Shops dafür Ihre Anmeldung brauchen. Sie prüfen alles, bevor es in den Beleg kommt.
          </p>
          <label className="wide">
            PDF oder Screenshot
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" disabled={files === false} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {files === false && <small className="muted-s">Das Lesen von Dateien ist auf diesem Server noch nicht eingerichtet. Der Text-Weg unten funktioniert.</small>}
          </label>
          <label className="wide">
            Oder Text des Warenkorbs einfügen
            <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Installationsrohr 20 mm\n2 x 11,90 €"} maxLength={60000} disabled={!!file} />
          </label>
          <label className="wide">
            Die Preise im Warenkorb sind
            <select value={gross ? "gross" : "net"} onChange={(e) => setGross(e.target.value === "gross")}>
              <option value="gross">Bruttopreise (mit Umsatzsteuer, wie in Shops für Privatkunden)</option>
              <option value="net">Nettopreise (ohne Umsatzsteuer, wie bei Großhändlern)</option>
            </select>
          </label>
          {file && (
            <label className="wide">
              <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Datei als Anhang am Beleg speichern (um den Preis später nachzuvollziehen)
            </label>
          )}
          <div className="wide">
            <Message>{error}</Message>
          </div>
          <div className="cu-buttons wide">
            <button type="button" className="dbtn ghost" onClick={props.onClose}>
              Abbrechen
            </button>
            <button type="button" className="dbtn" disabled={busy || (!file && !text.trim())} onClick={() => void read()}>
              {busy ? "Liest …" : "Einlesen"}
            </button>
          </div>
        </div>
      ) : (
        <div className="cu-form">
          <p className="muted-s wide">
            Das habe ich erkannt. Preise sind netto je Stück{gross ? " (aus den Bruttopreisen berechnet, bei 19 %)" : ""}. Bitte prüfen und bei Bedarf ändern. Mit „Hinweis“ markierte Zeilen waren unsicher.
          </p>
          <table className="cu-table dc-lines wide">
            <thead>
              <tr>
                <th>Position</th>
                <th>Menge</th>
                <th>Preis netto</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ cursor: "default" }}>
                  <td>
                    <input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} aria-label={`Bezeichnung ${i + 1}`} maxLength={200} />
                    {r.note && <small className="muted-s">Hinweis: {r.note}</small>}
                  </td>
                  <td>
                    <input className="num" value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} inputMode="decimal" aria-label="Menge" />
                  </td>
                  <td>
                    <input className="num" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} inputMode="decimal" aria-label="Preis netto" />
                  </td>
                  <td>
                    <button type="button" className="cu-link" onClick={() => setRows((rs) => (rs ? rs.filter((_, j) => j !== i) : rs))}>
                      Entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="wide">
            <Message>{error}</Message>
          </div>
          <div className="cu-buttons wide">
            <button type="button" className="dbtn ghost" onClick={() => { setRows(null); setError(""); }}>
              Zurück
            </button>
            <button type="button" className="dbtn" disabled={!rows.length} onClick={apply}>
              <Icon name="check" /> In den Beleg übernehmen
            </button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
